import * as z from "zod";
import { pagination } from "../index";
import { orderConfirmationDecisions, orderConfirmationRunStatuses, orderConfirmationStatuses } from "./enums";
import { reason } from "./fields";

// The order confirmations Nexxt issues (ADR-178 §1.8, ADR-198): the Customer Service asks for those of a brand and a
// season, and the process `order-confirmation` issues them a block at a time, each one a revision of its order with
// its PDF, and notifications sends it to the buyer (ADR-202).

/** The most orders a run names one by one: a brand and a season without them is every order that can have one. */
export const ORDER_CONFIRMATION_RUN_MAX_ORDERS = 1000;

/**
 * A run for a brand and a season, with the id from the client (ADR-046): asked again with the same id it answers the
 * run the first request left. Without `salesOrderIds`, every order of the brand and the season not cancelled nor
 * completed; with them, those, each of the brand and the season.
 */
export const issueOrderConfirmationsInput = z.object({
  id: z.uuid(),
  brandOrgId: z.uuid(),
  seasonId: z.uuid(),
  salesOrderIds: z.array(z.uuid()).min(1).max(ORDER_CONFIRMATION_RUN_MAX_ORDERS).optional(),
});
export type IssueOrderConfirmationsInput = z.input<typeof issueOrderConfirmationsInput>;

export const getOrderConfirmationRunInput = z.object({ runId: z.uuid() });

/** The runs by page, newest first: of every brand and season, or of one brand, or of a brand and a season. */
export const listOrderConfirmationRunsInput = z.object({
  brandOrgId: z.uuid().optional(),
  seasonId: z.uuid().optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListOrderConfirmationRunsInput = z.input<typeof listOrderConfirmationRunsInput>;
export type GetOrderConfirmationRunInput = z.input<typeof getOrderConfirmationRunInput>;

/** The confirmations of an order, the latest revision first. */
export const listOrderConfirmationsInput = z.object({ salesOrderId: z.uuid() });
export type ListOrderConfirmationsInput = z.input<typeof listOrderConfirmationsInput>;

export const orderConfirmationRunDto = z
  .object({
    id: z.uuid(),
    brandOrgId: z.uuid(),
    seasonId: z.uuid(),
    /** Their names, for the list of the runs (ADR-202). */
    brand: z.string(),
    season: z.string(),
    status: z.enum(orderConfirmationRunStatuses),
    /** The confirmations it asked for, and how many were issued and failed so far. */
    confirmations: z.number().int(),
    issued: z.number().int(),
    failed: z.number().int(),
    /** Who asked for it; null for a user without a name. */
    requestedBy: z.string().nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "OrderConfirmationRun" });
export type OrderConfirmationRunDto = z.output<typeof orderConfirmationRunDto>;

/** Held by the users of a customer (`customer_buyer`, ADR-208): they answer the confirmations of their orders. */
export const DECIDE_ORDER_CONFIRMATION = "order_confirmation.decide";

/** Where an answer came from, which the gateway of Frontrow reads as the access log does: never refused for it. */
const answeredFrom = {
  /** An address that is not one is dropped. */
  ipAddress: z.union([z.ipv4(), z.ipv6()]).nullish().catch(null),
  /** An agent too long is cut. */
  userAgent: z
    .string()
    .transform((agent) => agent.slice(0, 500))
    .nullish(),
};

const accepted = z.object({ decision: z.literal("accepted") });
const rejected = z.object({ decision: z.literal("rejected"), reason });

/**
 * The customer's answer as Frontrow sends it (ADR-221): the body of the route. Where it came from is never in it: the
 * gateway reads the address and the agent of the request.
 */
export const orderConfirmationAnswerBody = z
  .discriminatedUnion("decision", [accepted, rejected])
  .meta({ id: "OrderConfirmationAnswer" });
export type OrderConfirmationAnswerBody = z.input<typeof orderConfirmationAnswerBody>;

/** The revision a customer answers, in the path of the route. */
export const orderConfirmationParams = z.object({ confirmationId: z.uuid() });

/**
 * The customer's answer to a revision of its order (B-47, ADR-208): accepted, or rejected with its reason, which goes
 * to the Customer Service.
 */
export const decideOrderConfirmationInput = z.discriminatedUnion("decision", [
  accepted.extend({ ...orderConfirmationParams.shape, ...answeredFrom }),
  rejected.extend({ ...orderConfirmationParams.shape, ...answeredFrom }),
]);
export type DecideOrderConfirmationInput = z.input<typeof decideOrderConfirmationInput>;

/** What the customer answered to a revision: who, when and, for a rejection, why. */
export const orderConfirmationDecisionDto = z.object({
  decision: z.enum(orderConfirmationDecisions),
  /** Who answered; null for a user without a name. */
  decidedBy: z.string().nullable(),
  decidedAt: z.iso.datetime(),
  /** Why it was rejected; null for an acceptance. */
  reason: z.string().nullable(),
});
export type OrderConfirmationDecisionDto = z.output<typeof orderConfirmationDecisionDto>;

export const orderConfirmationDto = z
  .object({
    id: z.uuid(),
    salesOrderId: z.uuid(),
    /** The revision the customer receives: 1 the first, then one more for each one issued. */
    version: z.number().int(),
    status: z.enum(orderConfirmationStatuses),
    /** Issued with the template of an order with a deposit. */
    withDeposit: z.boolean(),
    /** The PDF, a document of the order (`GET /documents/documents/{documentId}/pdf`); null until issued. */
    documentId: z.uuid().nullable(),
    runId: z.uuid().nullable(),
    /** Why it failed: the code of the refusal, or `aborted` when its run gave up. */
    failure: z.string().nullable(),
    requestedAt: z.iso.datetime(),
    issuedAt: z.iso.datetime().nullable(),
    /** When a later revision was issued. */
    supersededAt: z.iso.datetime().nullable(),
    /**
     * Issued to a customer that could answer it, one with a user in Frontrow (ADR-208): it waits for an acceptance or
     * a rejection. Otherwise it counts as accepted, as in F1 (ADR-198 point 7).
     */
    asksAcceptance: z.boolean(),
    /** The customer's answer; null until it gives one. */
    decision: orderConfirmationDecisionDto.nullable(),
  })
  .meta({ id: "OrderConfirmation" });
export type OrderConfirmationDto = z.output<typeof orderConfirmationDto>;

// --- Process `order-confirmation` (ADR-029) ---

export const orderConfirmationRunParams = z.object({
  runId: z.uuid(),
  causedBy: z.uuid().nullable(),
  correlationId: z.string(),
});
export type OrderConfirmationRunParams = z.output<typeof orderConfirmationRunParams>;

/** Deterministic id of the instance: the request and its event start the same process. */
export const orderConfirmationRunInstance = (runId: string) => `order-confirmation-${runId}`;

export const orderConfirmationRunStepInput = z.object({ runId: z.uuid() });
